import { last, split } from 'lodash';
import {
  PHOTO_WITH_BG_PREFIX,
  TYPE_BEAUTY_FILTER_LIB,
  TYPE_DEEP_BEAUTY_FILTER_LIB,
} from '../constants/photo.const';
import { PickedPhotosModel } from '../models/common.model';
import { addSuffixToFilename } from './string.helper';

export const createImage = (url: string) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: any,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
) {
  const image = (await createImage(imageSrc)) as HTMLImageElement;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');

  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  // As Base64 string
  // return croppedCanvas.toDataURL('image/jpeg');

  // As a blob
  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob((file: any) => {
      resolve(URL.createObjectURL(file));
    }, 'image/png');
  });
}

export async function getRotatedImage(imageSrc: string, rotation = 0) {
  const image = (await createImage(imageSrc)) as HTMLImageElement;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

  const orientationChanged =
    rotation === 90 ||
    rotation === -90 ||
    rotation === 270 ||
    rotation === -270;
  if (orientationChanged) {
    canvas.width = image.height;
    canvas.height = image.width;
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  return new Promise((resolve) => {
    canvas.toBlob((file: any) => {
      resolve(URL.createObjectURL(file));
    }, 'image/png');
  });
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function getFileNameByPath(path: string = ''): string | undefined {
  if (!path) return path;

  return last(split(path, '/'));
}

export const getImageFileAfterEditPhoto = (
  filterId: number,
  filterType: number,
  backgroundId: number,
  ind: number,
  pickedPhotos: Array<PickedPhotosModel | null>,
) => {
  let imageFile = `${pickedPhotos?.[ind]?.fileName || ''}`;

  if (!!imageFile && backgroundId) {
    imageFile = `${PHOTO_WITH_BG_PREFIX}${imageFile}`;
  }

  if (!!imageFile && filterId && filterType === TYPE_BEAUTY_FILTER_LIB) {
    imageFile = addSuffixToFilename(imageFile, '_f1');
  }

  if (!!imageFile && filterId && filterType === TYPE_DEEP_BEAUTY_FILTER_LIB) {
    imageFile = addSuffixToFilename(imageFile, '_f2');
  }

  return imageFile;
};

export const getFileName = (
  filterId: number,
  filterType: number,
  fileName: string,
) => {
  if (filterId && filterType === TYPE_BEAUTY_FILTER_LIB) {
    return addSuffixToFilename(fileName, '_f1');
  }

  if (filterId && filterType === TYPE_DEEP_BEAUTY_FILTER_LIB) {
    return addSuffixToFilename(fileName, '_f2');
  }

  return fileName;
};

export const isVideoFile = (url: string | undefined) => {
  if (!url) return false;
  return url.toLowerCase().endsWith('.mp4');
};
